import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import MediaObjectsApi from "@/store/media-objects/-api";

@Module({
  name: "media-objects",
  stateFactory: true,
  namespaced: true,
})
export default class MediaObjectsStore extends VuexModule {
  mediaObjects: MediaObjectMap = {};

  get getMediaObjectById() {
    return (id: string | null | undefined) => {
      if (!id) {
        return;
      }

      return this.mediaObjects[id];
    };
  }

  @Action({ rawError: true })
  async fetchMediaObjectsByIds({ ids }: { ids: string[] }) {
    if (ids.length === 0) {
      return;
    }
    const api = new MediaObjectsApi(this.store.$axios);
    const response = await api.get({ ids });

    await processCoreModelResponse(this.store, response.data);
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchMediaObjectById(id: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.mediaObjects[id])) {
      return;
    }

    try {
      const api = new MediaObjectsApi(this.store.$axios);
      const response = await api.getOne(id);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return undefined;
      }
    }
  }

  @Mutation
  setMediaObjects({ mediaObjects }: { mediaObjects: MediaObjectMap }) {
    // Set core models first
    Vue.set(this, "mediaObjects", Object.assign({}, this.mediaObjects, mediaObjects));
  }
}

export const getMediaObjectsStore = (store: Store<unknown>) => {
  const mediaObjectsStore = getModule(MediaObjectsStore, store);

  return mediaObjectsStore;
};
